import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { projectKeys, recordKeys } from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import {
  isRecordListCache,
  type RecordCacheEntry,
} from "@/lib/records/record-cache";
import { findPendingRecord, markPendingRecordDeleted } from "@/offline/db";

interface BulkDeleteVars {
  projectId: string;
  recordIds: string[];
}

interface BulkDeleteContext {
  snapshots: ReadonlyArray<readonly [QueryKey, RecordCacheEntry | undefined]>;
}

/**
 * Optimistic bulk delete — the explicit exception to the non-optimistic
 * default in [[mutations.spec]] (see [[selection-mode.spec]] § Optimistic
 * bulk delete for why).
 *
 * Snapshots every cached records-list query for the project (there's one
 * per filter combination), removes the deleted IDs from each cache page,
 * fires `DELETE /projects/1.0/projects/{projectId}/records/bulk/`, then
 * rolls back on failure or invalidates on success. The projects list cache
 * also gets invalidated because record counts changed.
 *
 * Pending records (local outbox drafts the user multi-selected before they
 * synced) are split out and **marked `deleted`** in the outbox — the server
 * never had them, so including a pending `clientUuid` in the bulk `DELETE`
 * would be a doomed call against a record that does not exist. The `deleted`
 * flag (not a hard delete) lets the outbox drain reconcile an in-flight
 * create upload into a compensating server `DELETE` — a hard delete here
 * would orphan a ghost record on a lost-response retry. The grid filters
 * `deleted` entries out immediately so the tiles vanish at once. Only the
 * synced ids reach the server; if the whole selection is pending the server
 * call is skipped entirely. See [[offline-record-outbox]].
 *
 * The mutation hook does not own UI concerns (toasts, exiting selection
 * mode) — the caller passes those via the standard React Query
 * `mutate(..., { onSuccess, onError })` callbacks per [[mutations.spec]].
 */
export function useBulkDeleteRecords() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<{ deleted: number }, Error, BulkDeleteVars, BulkDeleteContext>({
    mutationFn: async ({ projectId, recordIds }) => {
      // Split pending (local outbox drafts) from synced ids. A pending
      // record's id IS its `clientUuid`; the server never had it.
      const pendingIds: string[] = [];
      const syncedIds: string[] = [];
      for (const id of recordIds) {
        // An unreadable outbox answers "not a draft" — see `findPendingRecord`.
        const pending = await findPendingRecord(id);
        if (pending) pendingIds.push(id);
        else syncedIds.push(id);
      }

      // Pending drafts are flagged `deleted` in the outbox (no hard delete)
      // so the drain can reconcile an in-flight upload — no server call here.
      await Promise.all(pendingIds.map((id) => markPendingRecordDeleted(id)));

      // Nothing synced selected → the whole selection was local drafts.
      if (syncedIds.length === 0) {
        return { deleted: pendingIds.length };
      }

      const result = await fetchWithAuth<{ deleted: number }>(
        `/projects/1.0/projects/${projectId}/records/bulk/`,
        {
          method: "DELETE",
          body: JSON.stringify({ record_ids: syncedIds }),
        },
      );
      return { deleted: result.deleted + pendingIds.length };
    },
    onMutate: async ({ projectId, recordIds }) => {
      const idsToDelete = new Set(recordIds);

      // Cancel in-flight fetches for any matching record-list query so they
      // can't overwrite our optimistic patch with stale data.
      await queryClient.cancelQueries({
        queryKey: recordKeys.byProject(projectId),
      });

      // Snapshot every matching cache (one per filter combination) so
      // rollback can restore them if the server rejects the delete.
      const queries = queryClient.getQueriesData<RecordCacheEntry>({
        queryKey: recordKeys.byProject(projectId),
      });
      const snapshots = queries.map(
        ([key, data]) => [key, data ? structuredClone(data) : undefined] as const,
      );

      // Patch each cache: drop the deleted IDs from every page, decrement
      // first-page count so the totalCount stays roughly honest in the UI
      // (the next refetch will sync it exactly).
      // A single record's own entry is left for the refetch below to drop.
      for (const [key] of queries) {
        queryClient.setQueryData<RecordCacheEntry>(key, (old) => {
          if (!isRecordListCache(old)) return old;
          return {
            ...old,
            pages: old.pages.map((page, index) => ({
              ...page,
              count:
                index === 0
                  ? Math.max(0, page.count - recordIds.length)
                  : page.count,
              results: page.results.filter(
                (record) => !idsToDelete.has(record.id),
              ),
            })),
          };
        });
      }

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _err, { projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: recordKeys.byProject(projectId),
      });
      // Project list & detail carry `records_count`; bulk delete just
      // changed it, so flush both.
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      // Reports holding these records show them as tombstones, and count less.
      invalidateReportContents(queryClient, projectId, { counts: true });
      // A bulk delete may have dropped pending outbox drafts — refresh the
      // grid's `["pendingRecords", projectId]` query and the always-mounted
      // drain's `["pendingRecords"]` query via the prefix.
      void queryClient.invalidateQueries({ queryKey: ["pendingRecords"] });
    },
  });
}
